import { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, CameraOff, Loader2 } from 'lucide-react';
import { usePostureDetection } from '@/hooks/usePostureDetection';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/lib/logger';

interface PostureCameraProps {
  isActive: boolean;
  onMetricsUpdate?: (metrics: any) => void;
  onIssuesUpdate?: (issues: any[]) => void;
}

export const PostureCamera = ({ isActive, onMetricsUpdate, onIssuesUpdate }: PostureCameraProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const {
    isDetecting,
    isInitialized,
    metrics,
    issues,
    initializePose,
    startDetection,
    stopDetection,
    cleanup
  } = usePostureDetection();

  useEffect(() => {
    if (metrics && onMetricsUpdate) {
      onMetricsUpdate(metrics);
    }
  }, [metrics, onMetricsUpdate]);

  useEffect(() => {
    if (issues && onIssuesUpdate) {
      onIssuesUpdate(issues);
    }
  }, [issues, onIssuesUpdate]);

  useEffect(() => {
    const initCamera = async () => {
      if (!isActive || !videoRef.current || !canvasRef.current) return;

      try {
        setIsLoading(true);
        await initializePose(videoRef.current, canvasRef.current);
        await startDetection();
        
        toast({
          title: "✅ Análise de Postura Ativada",
          description: "Posicione seu celular para que a câmera capture seu corpo todo."
        });
      } catch (error) {
        logger.error('Error initializing posture camera', error, { component: 'PostureCamera', action: 'initCamera' });
        toast({
          title: "❌ Erro ao Iniciar Câmera",
          description: "Verifique as permissões de câmera nas configurações.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (isActive) {
      initCamera();
    } else {
      stopDetection();
    }

    return () => {
      cleanup();
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <Card className="p-4 bg-background/95 backdrop-blur">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isDetecting ? (
              <Camera className="h-5 w-5 text-green-500 animate-pulse" />
            ) : (
              <CameraOff className="h-5 w-5 text-muted-foreground" />
            )}
            <h3 className="font-semibold">Análise de Postura</h3>
          </div>
          
          {isLoading && (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>

        <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            autoPlay
            muted
          />
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full"
            width={640}
            height={480}
          />
          
          {isDetecting && metrics && (
            <div className="absolute top-2 left-2 bg-black/70 text-white text-xs p-2 rounded space-y-1">
              <div>Score: {metrics.overallScore}/100</div>
              <div>Tronco: {metrics.trunkAngle.toFixed(1)}°</div>
              <div>Cadência: {metrics.cadence} p/min</div>
              <div>Pé: {metrics.footLandingType}</div>
            </div>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center">
          {isDetecting 
            ? "Análise em tempo real ativa. Mantenha o corpo visível na câmera."
            : "Iniciando análise de postura..."}
        </p>
      </div>
    </Card>
  );
};
